import type { DataType } from "@repo/lanes/types";
import type { ColumnFormat } from "@repo/shared/types/column-format";
import { SORT_TYPE_LABELS, type SortType } from "@repo/shared/types/sort";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/tooltip";

import { Icon } from "./icon";
import { TruncateTooltip } from "./truncate-tooltip";

/** Displays a column name with its format-type icon, and optional grouped/sort indicators. */
export const ColumnLabel = ({
  displayName,
  format,
  grouped,
  sortType,
  type,
  hideSort = false,
  hideGrouped = false,
  hideTypeIcon = false,
}: {
  displayName: string;
  /** When set, uses a type-specific sort icon (e.g. numeric vs alphabetical). */
  type: DataType;
  /** When set, uses a type-specific format icon (e.g. numeric vs alphabetical). */
  format: ColumnFormat;
  /** When true, shows a grouped indicator icon. */
  grouped: boolean;
  /** When set, shows a sort direction icon with a tooltip. */
  sortType: SortType | null;
  hideSort?: boolean;
  hideGrouped?: boolean;
  /** When true, suppresses the leading format-type icon. Use when the icon is rendered separately (e.g. as a drag handle). */
  hideTypeIcon?: boolean;
}) => {
  return (
    <div className="inline-flex min-w-0 flex-1 items-center gap-1">
      {!hideTypeIcon && <Icon.FormatType format={format} />}
      <TruncateTooltip className="text-foreground">
        {displayName}
      </TruncateTooltip>
      <div className="ml-auto flex items-center gap-1">
        {!hideGrouped && grouped && (
          <Tooltip>
            <TooltipTrigger
              render={<span />}
              className="inline-flex shrink-0 items-center"
            >
              <Icon name="stack-square" />
            </TooltipTrigger>
            <TooltipContent>Grouped column</TooltipContent>
          </Tooltip>
        )}
        {!hideSort && sortType && (
          <Tooltip>
            <TooltipTrigger
              render={<span />}
              className="inline-flex shrink-0 items-center"
            >
              <Icon.SortType sortType={sortType} type={type} />
            </TooltipTrigger>
            <TooltipContent>{SORT_TYPE_LABELS[sortType]} sort</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
};
